<template>
  <v-dialog
    :persistent="getUserFiles.length > 0 || getUploadStatus"
    v-model="uploadAssetDialog"
    width="100%"
    max-width="1000px"
  >
    <!-- Upload Assets Button in side panel. -->
    <template v-slot:activator="{ props}" v-model="dialog">
      <v-btn
        v-bind="props"
        variant="outlined"
        size="small"
        :prepend-icon="icons.upload"
        class="mr-1"
        color="primary"
      >
        Upload Assets
      </v-btn>
      
    </template>

    <!-- Drag and Drop Dialoge. -->
    <v-card
      @drop.prevent="onDrop($event)"
      @dragover.prevent="dragover = true"
      @dragenter.prevent="dragover = true"
      @dragleave.prevent="dragover = false"
      :class="{ 'grey-lighten-2': dragover }"
    >
      <!-- Upload Assets Dialoge title. -->
      <v-card-title
        v-if="getUserFiles.length === 0"
        class="bg-primary d-flex justify-center text-white"
      >
        <v-icon color="white" class="mr-4" size="small" me-auto>{{ icons.cloudUpload }}</v-icon>
        <span me-auto>Upload Assets</span>
        <v-spacer></v-spacer>
        <HelpButton :lexiconItem="lexicon.upload" :iconColor="'white'" class="pr-2"/>
        <v-icon @click="closeUploadDialog" > {{icons.cancel}} </v-icon>
      </v-card-title>

      <!-- Review Uploads Dialoge title. -->
      <v-card-title v-else class="bg-primary text-white">
        <v-icon color="white" class="mr-4" size="small">{{ icons.cloudUpload }}</v-icon>
        Review Uploads
      </v-card-title>

      <!-- Dialoge Body. -->
      <v-container fluid>

        <!-- Drag and Drop Area. -->
        <v-row
          v-if="getUserFiles.length === 0"
          style="border: dashed 2px rgb(var(--v-theme-darkGrey)); min-height: 300px;"
          class="d-flex flex-column ma-1 pa-3"
          density="compact"
          align="center"
          justify="center"
        >
          <v-icon
            color="primary"
            :class="[dragover ? 'mt-2, mb-6' : 'mt-5']"
            size="60"
          >
            {{ icons.cloudUpload }}
          </v-icon>
          <div class="text-center">
            <span class="text-body-1 font-weight-black"
              >Drag and drop files here</span
            >
            <br />
            (json, 3dm, svg, geojson, png...)
          </div>
          <span class="py-2">or</span>
          <v-btn @click="onFileUpload" size="large" class="bg-primary text-white"
            >Browse Files</v-btn
          >
          <input
            class="d-none"
            ref="uploader"
            type="file"
            multiple="multiple"
            @change="uploadFile($event)"
          />
        </v-row>

        <!-- Upload Review Area.-->
        <div v-else>
          <!-- Enhanced File Ingestion Progress Indicator -->
          <v-alert
            v-if="getIsIngesting"
            class="ma-1 py-2"
            color="info"
            icon="mdi:mdi-file-import"
          >
            <div class="d-flex flex-column">
              <div class="d-flex align-center">
                <span>Processing files: {{ getIngestedFilesCount }} / {{ getTotalFilesToIngest }}</span>
                <v-spacer></v-spacer>
                <v-progress-circular
                  indeterminate
                  color="white"
                  size="20"
                  width="2"
                  class="ml-2"
                ></v-progress-circular>
              </div>
              
              <!-- Additional processing details -->
              <div class="mt-2 text-caption">
                <div class="d-flex align-center">
                  <span>Activity: {{ getProcessingDetails.processingType }}</span>
                  <v-spacer></v-spacer>
                  <span>Batch: {{ getProcessingDetails.currentBatch }}/{{ getProcessingDetails.totalBatches }}</span>
                </div>
                
                <div v-if="getProcessingDetails.workerActive" class="d-flex align-center">
                  <v-chip class="mt-1 mr-2" color="success" size="x-small" label>Web Worker Active</v-chip>
                  <span class="text-truncate">Processing in background</span>
                </div>
                
                <div class="d-flex align-center mt-1">
                  <div class="d-flex flex-column" style="width: 100%">
                    <div class="d-flex justify-space-between">
                      <span>Batch progress:</span>
                      <span>{{ getProcessingDetails.batchProgress }}%</span>
                    </div>
                    <v-progress-linear
                      :model-value="getProcessingDetails.batchProgress"
                      color="white"
                      height="4"
                    ></v-progress-linear>
                  </div>
                </div>
                
                <div class="mt-1 text-truncate">
                  <small v-if="getProcessingDetails.lastProcessedFile">Last file: {{ getProcessingDetails.lastProcessedFile }}</small>
                </div>
                
                <div class="d-flex justify-space-between mt-1">
                  <v-chip class="mr-1" color="primary" size="x-small" label>
                    Processed: {{ getProcessingDetails.stats.processed }}
                  </v-chip>
                  <v-chip class="mr-1" color="warning" size="x-small" label>
                    Pending: {{ getProcessingDetails.stats.pending }}
                  </v-chip>
                  <v-chip class="mr-1" color="success" size="x-small" label>
                    Completed: {{ getProcessingDetails.stats.completed }}
                  </v-chip>
                  <v-chip v-if="getProcessingDetails.stats.failed > 0" color="error" size="x-small" label>
                    Failed: {{ getProcessingDetails.stats.failed }}
                  </v-chip>
                </div>
              </div>
            </div>
          </v-alert>

          <!-- Batch Files Warning -->
          <v-alert
            v-if="hasBatchFiles"
            class="ma-1 py-2"
            color="warning"
            icon="mdi:mdi-information-outline"
          >
            <div>
              <p>Large number of files detected. For performance reasons, some files have been grouped together.</p>
              <p>Any changes to type or tags will be applied to all files in the group.</p>
            </div>
          </v-alert>

          <!-- File Type Specification Alert. -->
          <v-alert
            class="ma-1 py-2"
            close-text="Close Alert"
            color="secondary"
            closable
          >
            <div>
              <span>Select how each file should be used in the notebook<br />
                <ul>
                  <li>
                    <small>
                      <a :href="lexicon.data.documentation" target="_blank" style="font-weight: bold; color: white">{{lexicon.data.plural}}</a> <i>(.json, .csv)</i>
                      can be visualized with multiple Widgets.
                    </small>
                  </li>
                  <li>
                    <small>
                      <a :href="lexicon.model.documentation" target="_blank" style="font-weight: bold; color: white">{{lexicon.model.plural}}</a> <i>(.3dm)</i>
                      are viewable in the 3D Widget
                    </small>
                  </li>
                  <li>
                    <small>
                      <a :href="lexicon.drawing.documentation" target="_blank" style="font-weight: bold; color: white">{{lexicon.drawing.plural}}</a> <i>(.svg)</i>
                      are viewable in the 2D Widget.
                    </small>
                  </li>
                  <li>
                    <small>
                      <a :href="lexicon.map.documentation" target="_blank" style="font-weight: bold; color: white">{{lexicon.map.plural}}</a> <i>(.geojson)</i>
                      are viewable in the Map Widget.
                    </small> 
                  </li>
                  <li>
                    <small>
                      <a :href="lexicon.image.documentation" target="_blank" style="font-weight: bold; color: white">{{lexicon.image.plural}}</a> <i>(.jpg, .jpeg, .png, .bmp, .gif)</i>
                      are viewable in the Image Grid Widget.
                    </small> 
                  </li>
                  <li>
                    <small>All other supported <a :href="lexicon.file.documentation" target="_blank" style="font-weight: bold; color: white">{{lexicon.file.plural}}</a>
                      formats can be added as Attachments to a Notebook.
                    </small>
                  </li>
                </ul>
              </span>
            </div>
          </v-alert>

          <!-- Table -->
           <v-table-virtual
            class="pt-2"
            density="compact"
            :key="groupUpdateKey"
            :disable-pagination="true"
            :hide-default-footer="true"
            max-height="300px"
          >

          <!-- Table Headers. -->
            <thead>
              <tr>
                <th v-for="header in uploadFilesHeaders" :key="header.text">
                  {{header.text}}
                </th>
              </tr>
            </thead>

            <!-- Table Body -->
            <tbody>

              <!-- Each Row. -->
              <tr v-for="(file, index) in getUserFiles" :key="index + '-' + getFileUpdateKey">

                <!-- Cancel Column. -->
                <td style="width: 4%">
                  <!-- For batch files use a different icon -->
                  <template v-if="file.isBatch">
                    <v-icon color="primary">mdi:mdi-database</v-icon>
                  </template>
                  <template v-else-if="file.isValidFile === false">
                    <v-icon  color="danger">{{ icons.error }}</v-icon>
                  </template>
                  <template v-else-if="file.uploadPercentage < 100 && file.uploadPercentage !== null">
                    <v-icon color="primary" icon="mdi:mdi-timer-sand"></v-icon>
                  </template>
                  <template v-else-if="file.uploadPercentage === 100">
                    <v-icon color="primary" @click="removeUserFile(file)">{{ icons.check }}</v-icon>
                  </template>
                  <template v-else>
                    <v-icon  color="darkGrey" @click="removeUserFile(file)">{{ icons.cancel }}</v-icon>
                  </template>
                </td>

                <!-- File Name Column. -->
                <td class="file-name-cell text-truncate">
                  <!-- Special display for batch files -->
                  <template v-if="file.isBatch">
                    <v-icon size="small" color="warning">mdi:mdi-folder-multiple-image</v-icon>
                    <strong>
                      &nbsp; {{ file.name }} 
                      <span v-if="file.completedCount > 0" class="text-caption">
                        ({{ file.completedCount }}/{{ file.batchCount }})
                      </span>
                    </strong>
                  </template>
                  <template v-else>
                    <v-icon size="x-small" color="darkGrey" :key="fileIconKey+index">{{ file.icon }}</v-icon>
                    <small>
                      &nbsp; {{ file.name }}
                      <v-tooltip activator="parent" location="top">{{file.name}}</v-tooltip>
                    </small>
                  </template>
                  <div>
      
                    <v-progress-linear
                      class="mt-0"
                        v-if="file.uploadPercentage !== null"
                        height="8"
                        rounded
                        v-model="file.uploadPercentage"
                        :key="getFileUpdateKey"
                        :color="file.isValidFile ? 'secondary' : 'danger'"
                      >
                        <small
                          style="
                            color: white;
                            font-size: xx-small;
                            font-weight: 500;
                          "
                        >
                          {{ Math.ceil(file.uploadPercentage) }}%</small
                        >
                    </v-progress-linear>
                  </div>
                </td>  

                <!-- Tags Column. -->
                <td style="width: 25%">
                    <v-combobox
                      v-model="file.tags"
                      :items="tagOptions"
                      multiple
                      chips
                      density="compact"
                      @update:modelValue="onTagChange(file)"
                    ></v-combobox>
                </td>

                <!-- Options Column. -->
                <td style="width: 4%">

                  <!-- For batch files, show a batch info icon -->
                  <template v-if="file.isBatch">
                    <v-tooltip location="top">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" color="info" size="small">mdi:mdi-information-outline</v-icon>
                      </template>
                      <div>
                        <p>This is a grouped batch of {{ file.batchCount }} {{ file.extension }} files.</p>
                        <p>All files will be processed as {{ file.dataType }} type.</p>
                      </div>
                    </v-tooltip>
                  </template>
                  <!-- if File has Upload Options, Show Virtical ellipses button with Optins.-->
                  <v-menu v-else-if="file.uploadOptions" offset->
                    <!-- Button & Icon. -->
                    <template v-slot:activator="{ props}">
                      <v-btn v-bind="props"  size="x-small" icon variant="text">
                        <v-icon size="medium" class="mx-1 mr-2">{{ icons.menu }}</v-icon></v-btn>
                    </template>

                    <!-- Popup Menu -->
                    <v-card>
                      <!-- For each upload option key, add a checkbox tied to value -->
                      <v-list-item v-for="(value, key) in file.uploadOptions" :key="key">
                        <v-btn
                          @click="file.uploadOptions[key](file)"
                          text
                          class="text-start"
                        >
                          {{ formatLabel(key) }}
                        </v-btn>
                      </v-list-item>
                    </v-card>
                  </v-menu>

                  <!-- If no upload options, show a dash. -->
                  <template v-else>
                    <span class="ml-4 text-caption">-</span>
                  </template>
                </td>
                
                <!-- Size Column. -->
                <td style="width: 14%">
                  <!-- as mb-->
                  <small>{{ formatBytes(file.size, 2) }}</small>
                </td>
                  
                <!-- Data Type Column (Dropdown). -->
                <td style="width: 20%">
                  <v-select
                      :key="dataTypeKey"
                      :items="file.dataTypeOptions || ['Asset']"
                      v-model="file.dataType"
                      @update:modelValue="(value) => onDataTypeChange(file, value)"
                      :disabled="!file.dataTypeOptions || file.dataTypeOptions.length === 0"
                  >
                  </v-select>
                </td>
              </tr>
            </tbody>
          </v-table-virtual>

          <!-- Bottom Buttons. -->
          <div class="d-flex pt-5 justify-end">
            <template v-if="getFileUploadDone">
              <v-btn
              color="primary"
              @click="closeUploadDialog"
              >Done</v-btn
            >
            </template>
            <template v-else>
              <v-btn
              class="mr-2"
              :disabled="cancelUploadDisabled"
              @click="cancelUpload"
            >
              Exit
            </v-btn>
            <v-btn
              color="primary"
              :loading="getUploadStatus || uploadButtonDisabled"
              :disabled="getUploadStatus || uploadButtonDisabled"
              @click="startUpload"
            >
              Upload
            </v-btn>
            </template>
          </div>
        </div>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import * as lexicon from "@/utilities/EllipseLexicon.js";
import * as icons from "@/utilities/EllipseIcons.js";
import PillButton from "@/components/ui/PillButton.vue";
import ProgressCircular from "../components/ui/ProgressCircular.vue"
import HelpButton from "../components/ui/HelpButton.vue";
import {storeToRefs } from 'pinia';
import {useNotebookPropsStore } from "@/store/NotebookPropsStore.js";
import {useAssetsStore } from "@/store/AssetsStore.js";
import {useEllipseStore } from "@/store/EllipseStore.js";
import {useDataUploadStore } from "../store/DataUploadStore";
import {formatBytes } from "@/utilities";

export default {
  components: { PillButton, ProgressCircular, HelpButton },
  setup() {
    const dataUploadStore = useDataUploadStore()
    const { 
      getUserFiles,
      getUploadStatus,
      getFileUpdateKey,
      getFileUploadDone,
      getTotalFilesToIngest,
      getIngestedFilesCount,
      getIsIngesting,
      getFileBatches,
      getProcessingDetails,
    } = storeToRefs(dataUploadStore);

    const { 
      getDataTypeOptions,
      ingestUserFiles,
      startUpload,
      removeUserFile,
      clearUserFiles,
      triggerFileUpdateKey,
      updateFileUploadType,
      updateFileTags,
    } = dataUploadStore;

    const notebookPropsStore = useNotebookPropsStore()
    const {
      getSelectedNavigationTab,
      getSelectedPage,
    } = storeToRefs(notebookPropsStore)

    const ellipseStore = useEllipseStore()
    const {
      getNotebook
    } = storeToRefs(ellipseStore)

    const assetsStore = useAssetsStore()
    const {
      getAllAssetsInNotebook,
      getSelectedPageDatasets,
      getSelectedPageDataset,
    } = storeToRefs(assetsStore)

    return {
      notebookPropsStore,
      getSelectedNavigationTab,
      getAllAssetsInNotebook,
      getSelectedPageDatasets,
      getSelectedPageDataset,
      getSelectedPage,
      getNotebook,
      lexicon,
      icons,
      getUserFiles,
      getUploadStatus,
      ingestUserFiles,
      startUpload,
      removeUserFile,
      clearUserFiles,
      getDataTypeOptions,
      triggerFileUpdateKey,
      getFileUpdateKey,
      getFileUploadDone,
      getTotalFilesToIngest,
      getIngestedFilesCount,
      getIsIngesting,
      getFileBatches,
      updateFileUploadType,
      updateFileTags,
      getProcessingDetails,
    }
  },
  data() {
    return {
      tagOptions: [],
      dragover: false,
      uploadedFiles: [],
      uploadAssetDialog: false,
      uploadFilesHeaders: [
        { text: "", value: "", align: "center" },
        { text: "File Name", value: "name", align: "center" },
        { text: "Tags", value: "tags", align: "center" },
        { text: "Options", value: "options", align: "center" },
        { text: "Size", value: "size", align: "center" },
        { text: "Type", value: "dataType", align: "center" },
      ],
      groupUpdateKey: 0,
      dataTypeKey: 1,
      fileIconKey: 0,
      dialog: false,
    };
  },
  computed: {
    cancelUploadDisabled() {
      return this.getUploadStatus; 
    },  
    uploadButtonDisabled() {
      // Disable button after upload completes but before fileUploadDone is true
      return this.getUserFiles.some(file => file.uploadPercentage === 100) &&
             !this.getFileUploadDone;
    },
    fileUpdateKey() {
      return this.getFileUpdateKey;
    },
    displayTable() {
      return this.uploadedFiles.length > 0;
    },
    hasBatchFiles() {
      return this.getUserFiles.some(file => file.isBatch);
    }
  },
  created() {
    if (
      this.getAllAssetsInNotebook.datasets.length === 0 &&
      this.getAllAssetsInNotebook.drawings.length === 0 &&
      this.getAllAssetsInNotebook.models.length === 0 &&
      this.getSelectedNavigationTab === "globalSources"
    ) {
      this.uploadAssetDialog = true;
    }
  },
  destroyed() {
    this.clearUserFiles();
  },
  methods: {
    cancelUpload() {
      this.uploadAssetDialog = false;
      this.clearUserFiles();
    },
    closeUploadDialog() {
      this.clearUserFiles();
      this.uploadAssetDialog = false;
    },
    updateDataType() {
      this.dataTypeKey += 1;
    },
    onDataTypeChange(file, value) {
      this.updateFileUploadType(file, value);
      this.updateDataType();
    },
    onFileUpload() {
      this.$refs.uploader.click();
    },
    async uploadFile(e) {
      this.ingestUserFiles(e.target.files);
    },
    async onDrop(e) {
      this.dragover = false;
      this.ingestUserFiles(e.dataTransfer.files);
    },
    formatBytes(bytes, decimals = 2) {
      return formatBytes(bytes, decimals);
    },
    formatLabel(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    onTagChange(file) {
      this.triggerFileUpdateKey();
      this.updateFileTags(file, file.tags);
      
      file.tags.forEach(tag => {
        if (!this.tagOptions.includes(tag)) {
          this.tagOptions.push(tag);
        }
      });
    },
  }
};
</script>

<style scoped>
</style>
